/**
 * MYAC-904 report-v2 polish — "one card per subscribed LINE" (user: "312
 * lignes suivies should give us 312 lignes in the report").
 *
 * Backend v2 report entries are FAMILY-deduplicated: `subscription_row_ids`
 * lists every `regulation_lists` row that subscribes to the same family
 * (spec-intentional — one narrative/edition per family, not per row). Users
 * find the resulting mismatch ("312 réglementations" in the header vs ~281
 * cards in the sections) confusing. This is a DISPLAY-LAYER fix only: never
 * touch the backend/report docs, never touch `subscription_row_ids` itself —
 * just expand each entry into one DISPLAY CARD per row it covers, at render
 * time, on the front.
 *
 * Pure function: (entries, rowsById) -> display cards. No Firestore, no
 * store access — callers (the web view, the store's `reviewProgressV2`
 * getter, the PDF export) pass in whatever `regulation_lists` rows they have
 * loaded.
 */

/** Minimal shape of a v2 report entry needed to expand it — matches
 *  `ReportEntryV2` structurally without importing the class (keeps this
 *  util trivially testable with plain fixtures). */
export interface ExpandableReportEntryV2 {
  id: string
  designation: string
  subscription_row_ids: string[]
}

/** Minimal shape of a `regulation_lists` row needed to resolve a card's
 *  heading — matches `RegulationListItem` structurally. */
export interface ExpandableRegulationRowV2 {
  id: string
  designation: string
}

/** Lookup for rows by id — a `Map` (preferred, O(1) and explicit) or a plain
 *  `Record` (convenient at call sites that already have one). */
export type RegulationRowLookup<TRow extends ExpandableRegulationRowV2> =
  | Map<string, TRow>
  | Record<string, TRow | undefined>

function lookupRow<TRow extends ExpandableRegulationRowV2>(
  rowsById: RegulationRowLookup<TRow>,
  rowId: string,
): TRow | undefined {
  return rowsById instanceof Map ? rowsById.get(rowId) : rowsById[rowId]
}

/** One rendered card. `entry` is always the SAME shared entry for every card
 *  derived from it — all non-heading content (edition, changes, links,
 *  review state) and review INTERACTIONS stay entry-level (a review click on
 *  any sibling card acts on the shared entry, writing to every subscribing
 *  row — existing, correct behaviour, untouched by this expansion). */
export interface ReportDisplayCardV2<TEntry extends ExpandableReportEntryV2> {
  /** Stable v-for/PDF-ordering key. The FIRST resolved row of an entry keeps
   *  the entry's own id (bare, no suffix) — this preserves the existing
   *  DOM-anchor id (`reg-entry-v2-{id}`) and deep-link scroll target for the
   *  common single-row case; additional sibling rows get `{id}::{rowId}`. */
  key: string
  entry: TEntry
  /** What to render as the card's heading — the resolving row's OWN
   *  designation, or (stale/unresolved fallback) the entry's own
   *  designation. Never empty when the entry's designation is set. */
  heading: string
  /** The entry's own canonical designation, shown as a secondary/tooltip
   *  line — ONLY when it differs from `heading` (i.e. only on a resolved
   *  row whose typed designation differs from the catalogue's). `null`
   *  when there's nothing extra to show (fallback card, or an exact match). */
  secondaryDesignation: string | null
  /** The row this card represents, or `null` for the stale/unresolved
   *  fallback card (no row_id resolved locally). */
  rowId: string | null
}

/**
 * Expands v2 report entries into one display card per resolvable
 * `subscription_row_ids` element.
 *
 * - Multi-row entry, all rows resolve: one card per row, headed by each
 *   row's own designation.
 * - Stale row_id (row deleted+recreated since the report was generated — the
 *   edition-pinning mechanism, this HAPPENS): that row_id is skipped, never
 *   dropping the entry.
 * - NONE of an entry's row_ids resolve (all stale, or the entry has none at
 *   all): ONE fallback card, headed by the entry's own designation — the
 *   entry is never dropped and never rendered with a blank heading.
 * - Counts reconcile: `cards.length` summed across every category is the
 *   EXPANDED (row) count, which is what the KPI tiles / section headers /
 *   review-progress denominator should use instead of `entries.length`.
 */
export function expandReportEntriesV2ToCards<
  TEntry extends ExpandableReportEntryV2,
  TRow extends ExpandableRegulationRowV2,
>(entries: TEntry[], rowsById: RegulationRowLookup<TRow>): ReportDisplayCardV2<TEntry>[] {
  const cards: ReportDisplayCardV2<TEntry>[] = []

  for (const entry of entries) {
    const resolved = entry.subscription_row_ids
      .map((rowId) => ({ rowId, row: lookupRow(rowsById, rowId) }))
      .filter((r): r is { rowId: string; row: TRow } => r.row !== undefined)

    if (resolved.length === 0) {
      // No row_ids at all, or every one of them is stale — never drop the
      // entry, render it once under its own canonical designation.
      cards.push({
        key: entry.id,
        entry,
        heading: entry.designation,
        secondaryDesignation: null,
        rowId: null,
      })
      continue
    }

    resolved.forEach(({ rowId, row }, index) => {
      const heading = row.designation || entry.designation
      const secondaryDesignation =
        heading !== entry.designation && entry.designation ? entry.designation : null
      cards.push({
        // First resolved row keeps the bare entry id (DOM-anchor/deep-link
        // compatibility with the pre-expansion single-card behaviour).
        key: index === 0 ? entry.id : `${entry.id}::${rowId}`,
        entry,
        heading,
        secondaryDesignation,
        rowId,
      })
    })
  }

  return cards
}
